<template>
	<div class="activity-card" :class="{ selected }">
		<button type="button" class="select-action" :aria-pressed="selected" @click="emit('select')">
			<span
				class="activity-icon"
				:class="activityIconClassByNotificationTopic[activity.notificationTopic]"
				aria-hidden="true"
			>
				<span class="icon"></span>
			</span>

			<div class="content">
				<p class="title" :class="{ read: activity.readAt }">{{ activity.title }}</p>

				<div class="details">
					<span class="linked-resource">
						<span class="link-icon" :class="linkIconClassByType[activity.linkType]" aria-hidden="true"></span>
						<span class="link-name">{{ linkName }}</span>
					</span>
					<time :datetime="activity.timestamp.toISOString()">{{ formattedTimestamp }}</time>
				</div>
			</div>

			<span v-if="!activity.readAt" class="unread-indicator" aria-hidden="true"></span>
		</button>

		<button
			type="button"
			class="delete-action"
			:title="t('activityPage.delete')"
			:aria-label="t('activityPage.delete')"
			@click="emit('delete')"
		>
			<span class="icon" aria-hidden="true"></span>
		</button>
	</div>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { useNow } from "@vueuse/core"
import { useI18n } from "vue-i18n"
import { NotificationTopic } from "@/graphql"
import { type Activity, LinkType } from "@/types/activity"
import { formatRelativeDateTime } from "@/utils/date"

const props = defineProps<{
	activity: Activity
	selected?: boolean
}>()
const emit = defineEmits<{
	select: []
	delete: []
}>()
const { locale, t } = useI18n()
const now = useNow({ interval: 60_000 })

const activityIconClassByNotificationTopic: Record<NotificationTopic, string> = {
	[NotificationTopic.CommentedOpportunity]: "comment",
	[NotificationTopic.DislikedOpportunity]: "dislike",
	[NotificationTopic.LikedOpportunity]: "like",
	[NotificationTopic.Other]: "new-target",
	[NotificationTopic.SavedSearchNewOpportunity]: "new-listing",
	[NotificationTopic.TargetCreated]: "new-target",
}

const linkIconClassByType: Record<LinkType, string> = {
	[LinkType.PROJECT]: "project",
	[LinkType.SAVED_SEARCH]: "saved-search",
}

const linkName = computed(() => {
	if (props.activity.linkType === LinkType.PROJECT) {
		return props.activity.project
	}

	return props.activity.savedSearch
})

const formattedTimestamp = computed(() =>
	formatRelativeDateTime(props.activity.timestamp, now.value, locale.value, (key, values) => t(key, values))
)
</script>

<style scoped>
.activity-card {
	display: flex;
	align-items: center;
	gap: var(--space-12);
	width: 100%;
	padding: var(--spacing-card-padding-sm);
	border: var(--border-width-thin) solid transparent;
	border-radius: var(--border-radius-lg);
	background: var(--neutral-0);
	color: inherit;

	&:focus-within {
		border-color: var(--neutral-200);
	}

	&:hover {
		border-color: var(--neutral-200);
		background: var(--neutral-100);
	}

	&.selected {
		border-color: var(--neutral-200);
		background: var(--neutral-0);
	}

	&.selected:hover {
		background: var(--neutral-100);
	}
}

.select-action {
	display: flex;
	align-items: center;
	gap: var(--space-12);
	min-width: 0;
	padding: 0;
	border: 0;
	background: transparent;
	color: inherit;
	font-family: var(--font-family-primary);
	text-align: left;
	cursor: pointer;
	flex: 1;

	&:focus {
		outline: none;
	}
}

.delete-action {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: var(--size-button);
	height: var(--size-button);
	padding: 0;
	border: 0;
	border-radius: var(--border-radius-md);
	background: transparent;
	color: var(--neutral-400);
	cursor: pointer;
	flex: 0 0 auto;

	.icon {
		width: var(--size-icon-md);
		height: var(--size-icon-md);
		background: currentColor;
		mask: url("/icons/activity-delete.svg") center / contain no-repeat;
	}

	&:hover,
	&:focus-visible {
		background: color-mix(in srgb, var(--accent-warning) 10%, var(--neutral-0));
		color: var(--accent-warning);
		outline: none;
	}
}

.activity-icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: var(--size-button-lg);
	height: var(--size-button-lg);
	border-radius: var(--border-radius-lg);
	flex: 0 0 auto;

	.icon {
		width: var(--size-icon-lg);
		height: var(--size-icon-lg);
		background: currentColor;
		mask: center / contain no-repeat;
	}

	&.comment {
		background: var(--accent-info-background);
		color: var(--accent-file);

		.icon {
			mask-image: url("/icons/activity-comment.svg");
		}
	}

	&.new-listing {
		background: color-mix(in srgb, var(--primary-green) 8%, var(--neutral-0));
		color: var(--primary-green);

		.icon {
			mask-image: url("/icons/activity-new-listing.svg");
		}
	}

	&.new-target {
		background: color-mix(in srgb, var(--accent-target) 10%, var(--neutral-0));
		color: var(--accent-target);

		.icon {
			mask-image: url("/icons/activity-new-target.svg");
		}
	}

	&.like {
		background: color-mix(in srgb, var(--accent-dev-potential-medium) 12%, var(--neutral-0));
		color: var(--accent-dev-potential-medium);

		.icon {
			mask-image: url("/icons/activity-like.svg");
		}
	}

	&.dislike {
		background: var(--neutral-100);
		color: var(--neutral-600);

		.icon {
			mask-image: url("/icons/activity-dislike.svg");
		}
	}
}

.content {
	display: flex;
	flex: 1;
	flex-direction: column;
	gap: var(--spacing-xs);
	min-width: 0;
}

.unread-indicator {
	width: var(--space-8);
	height: var(--space-8);
	border-radius: var(--border-radius-full);
	background: var(--primary-green);
	flex: 0 0 auto;
}

.title {
	color: var(--neutral-900);
	font-size: var(--font-size-regular);
	font-weight: var(--font-weight-semibold);
	line-height: var(--line-height-tight);

	&.read {
		font-weight: var(--font-weight-regular);
	}
}

.details {
	display: flex;
	align-items: center;
	gap: var(--spacing-sm);
	min-width: 0;
	color: var(--neutral-600);
	font-size: var(--font-size-xs);
}

.linked-resource {
	display: inline-flex;
	align-items: center;
	gap: var(--spacing-xs);
	min-width: 0;
	padding: var(--spacing-xxs) var(--spacing-sm);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-full);
	background: var(--neutral-0);
	color: var(--neutral-800);
	font-size: var(--font-size-xs);
	font-weight: var(--font-weight-medium);
	line-height: var(--line-height-tight);

	.link-icon {
		width: var(--space-12);
		height: var(--space-12);
		background: currentColor;
		mask: center / contain no-repeat;
		flex: 0 0 auto;

		&.project {
			mask-image: url("/icons/activity-link-project.svg");
		}

		&.saved-search {
			mask-image: url("/icons/activity-link-saved-search.svg");
		}
	}

	.link-name {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
}

time {
	flex: 0 0 auto;
	white-space: nowrap;
}
</style>
